/// <amd-module name="Wit/Common/BacklogContentService/ISprintsHubContentService" />
import { IBacklogConfiguration } from "Agile/Common/Redux/BacklogConfiguration/BacklogConfigurationContracts";
import * as React from "react";
import { TeamContext } from "Tfs/Platform/Contracts/Core";
import { getTFSPageData, getTFSProject, ITfsPageService } from "Tfs/Platform/Page";
import { equals, format } from "VSS/Core/Util/String";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssHistoryService } from "VSS/Platform/History";
import { routeUrl } from "VSS/Platform/Location";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { Link } from "VSSUI/Link";
import { MessageCardSeverity } from "VSSUI/MessageCard";
import { ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";
import { TreeStructureGroup, WorkItemClassificationNode } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { IWorkItemTrackingRestClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking.Types";
import { getWorkClient } from "Wit/Clients/Work/RestClient/Work";
import * as Resources from "Wit/Common/BacklogContentService/Resources";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { ExceptionInfoResult } from "Wit/Common/HubError/Models/Models";
import { normalizePath } from "Wit/Common/WorkItemControls/NodeUtils";
import { IWorkItemNodeService, NodeConstants } from "Wit/Common/WorkItemNodeService/IWorkItemNodeService";
import { FilterManager } from "Wit/Filter/FilterManager";
import { getBoardsMessagesService } from "Wit/Messages/BoardsMessagesService";
import { ContainerNames } from "Wit/Messages/Components/BoardsMessages";
import { SprintsBacklogColumnSettings } from "./BacklogContentContants";
import { IBacklogColumn, ICommonBacklogData, ISprintsBacklogData } from "./BacklogContentDataProviderTypes";
import { HubType, IBacklogTreeRow } from "./BacklogContentTreeTypes";
import { IBacklogWorkItemService } from "./IBacklogWorkItemService";
import { ICommonBacklogContentService, isExceptionInfoResult } from "./ICommonBacklogContentService";
import { IHubContentService } from "./IHubContentService";

const BacklogsRoute = "ms.vss-work-web.new-backlogs-route";
const SprintBacklogContentDataProvider = "ms.vss-work-web.sprints-hub-backlog-data-provider";

namespace SprintsHubSettings {
    export const IterationKey = "Iteration";
    export const NavigationKey = "Agile/SprintsHub/Navigation";
    export const ProjectKey = "Project";
    export const SprintsFilterKey = "Sprints/backlog/Filter";
}

export interface SprintHubCalcificationNode extends WorkItemClassificationNode {
    friendlyPath: string;
}

export interface ISprintsHubContentService extends IVssService, IHubContentService {
    getCurrentIteration(): SprintHubCalcificationNode | undefined;
}

class SprintsHubContentService extends VssService implements ISprintsHubContentService {
    private allIterationNodes: WorkItemClassificationNode[];
    private currentIteration: SprintHubCalcificationNode | undefined;
    private commonBacklogContentService: ICommonBacklogContentService;
    private parentItems: Map<number, string> | undefined;
    private iterationToIterationNodeMap = new Map<string, WorkItemClassificationNode | undefined>();
    private iterationPromiseToIterationNodeMap = new Map<string, Promise<WorkItemClassificationNode | undefined>>();
    public isReorderDisabled: boolean = false;
    public workItemsBlockingReorder: number[] = [];
    public missingWorkItems: number[] = [];

    public async _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        this.commonBacklogContentService = this.pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");
        this.commonBacklogContentService.setHubType(HubType.Sprints);

        await this.getIteration();
    }

    public _serviceEnd(pageContext: IVssPageContext) {
        super._serviceEnd(pageContext);
    }

    public getBacklogContents = async (options?: { ignoreCache: boolean }): Promise<ICommonBacklogData | ExceptionInfoResult> => {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const result = await contributionService.getDataAsync<ISprintsBacklogData | ExceptionInfoResult>(
            SprintBacklogContentDataProvider,
            {},
            options?.ignoreCache
        );

        if (isExceptionInfoResult(result)) {
            return result;
        } else {
            return {
                backlogQueryResults: result?.backlogQueryResults,
                backlogLevelId: result?.backlogContext.levelName ?? "",
                backlogWiql: result?.backlogQueryResults.wiql ?? "",
                backlogWorkItemTypeNames: result?.activeWorkItemTypes ?? [],
                initialBacklogFilterJson: result?.initialBacklogFilterJson ?? "",
                columnPreferences: result?.columnPreferences ?? []
            };
        }
    };

    public getFilterKey = () => {
        return SprintsHubSettings.SprintsFilterKey;
    };

    public getZeroDataPrimaryText = () => {
        return Resources.SprintsZeroDataPrimaryText;
    };

    public getZeroDataSecondaryText = () => {
        return (
            <div>
                <Link className="flex-center" href={this.getBacklogUrl()}>
                    {Resources.SprintsZeroDataScheduleLink}
                </Link>
                <span>{Resources.SprintsZeroDataSecondaryText}</span>
            </div>
        );
    };

    public getCurrentIteration = () => {
        return this.currentIteration;
    };

    public onIterationChange = (workItems: ITreeItemEx<IBacklogTreeRow>[]) => {
        workItems = workItems.filter(item => {
            const isIterationDifferent =
                (item.underlyingItem.data.value.coreColumnMap[CoreFieldRefNames.IterationId] as number) !== this.currentIteration?.id;
            const haveChildren = item.underlyingItem.data.children;
            const childIterations = item.underlyingItem.data.children?.map(
                child => child.value.coreColumnMap[CoreFieldRefNames.IterationId] as number
            );
            const childIterationsMatchCurrent = childIterations?.find(cit => cit === this.currentIteration?.id);
            return isIterationDifferent && (!haveChildren || !childIterationsMatchCurrent);
        });

        const backlogWorkItemService = this.pageContext.getService<IBacklogWorkItemService>("IBacklogWorkItemService");
        backlogWorkItemService.updateBacklogTreeRowsDelete(workItems);

        backlogWorkItemService.getFilterManager().dataUpdated();
        this.commonBacklogContentService.resetOrderColumn();
    };

    public refreshTree = async (options?: { keepOpen?: boolean; filterManager?: FilterManager; ignoreBacklogConfigCache?: boolean }) => {
        this.commonBacklogContentService.getAllIdsToRowsMap().clear();
        const backlogWorkItemService = this.pageContext.getService<IBacklogWorkItemService>("IBacklogWorkItemService");
        backlogWorkItemService.clearCache();

        await this.getIteration();

        const settingsService = this.pageContext.getService<ISettingsService>("ISettingsService");
        const projectId = getTFSPageData(this.pageContext)?.project?.id ?? "";
        const key = `${SprintsHubSettings.NavigationKey}/${SprintsHubSettings.IterationKey}`;
        await settingsService.setEntries(
            { [key]: this.currentIteration?.identifier },
            SettingsUserScope.Me,
            SprintsHubSettings.ProjectKey,
            projectId
        );
        await this.commonBacklogContentService.refreshTree({
            getData: () => this.getBacklogContents({ ignoreCache: true }),
            keepOpen: options?.keepOpen,
            filterManager: options?.filterManager
        });
        this.updateReorderRequirementStatus(options?.ignoreBacklogConfigCache);
    };

    public settingsServiceGetColumns = async (): Promise<IBacklogColumn[]> => {
        return this.commonBacklogContentService.settingsServiceGetColumns(
            SprintsBacklogColumnSettings.ColumnOptions,
            SprintsBacklogColumnSettings.IterationKey
        );
    };

    public settingsServiceSetColumns = async (backlogColumns: IBacklogColumn[]): Promise<void> => {
        return this.commonBacklogContentService.settingsServiceSetColumns(
            SprintsBacklogColumnSettings.ColumnOptionsKey,
            backlogColumns,
            SprintsBacklogColumnSettings.IterationKey
        );
    };

    public settingsServiceResizeColumn = async (resizedColumnId: number, newWidth: number): Promise<void> => {
        return this.commonBacklogContentService.settingsServiceResizeColumn(
            SprintsBacklogColumnSettings.ColumnOptions,
            resizedColumnId,
            newWidth,
            SprintsBacklogColumnSettings.IterationKey
        );
    };

    private getBacklogUrl = () => {
        const routeValues: { [key: string]: string } = {};
        routeValues["team"] = this.commonBacklogContentService.getCurrentTeam().name;
        routeValues["project"] = getTFSPageData(this.pageContext)?.project?.name || "";
        return routeUrl(this.pageContext, BacklogsRoute, routeValues);
    };

    private getIteration = async () => {
        if (!isFeatureFlagEnabled(this.pageContext, FeatureFlags.SprintsHubPullOneIterationAtATimeEnabled, false)) {
            const nodeService = this.pageContext.getService<IWorkItemNodeService>("IWorkItemNodeService");
            const iterationNode = await nodeService.getIterationNodes(getTFSProject(this.pageContext)?.id ?? "");
            const iteration = this.pageContext.getService<IVssHistoryService>("IVssHistoryService").getState()["iteration"];

            if (iterationNode) {
                const convertedIterationPath = this.constructIterationPath(iteration);
                this.allIterationNodes = flattenNodes(iterationNode);

                let currentIterationNode = this.allIterationNodes.find(node => normalizePath(node.path) === normalizePath(convertedIterationPath));
                
                this.currentIteration = currentIterationNode
                    ? { ...currentIterationNode, friendlyPath: iteration.replace(/\//g, "\\") /* replace all slashes with backslashes */ }
                    : undefined;
            }
        } else {
            const iteration = this.pageContext.getService<IVssHistoryService>("IVssHistoryService").getState()["iteration"];

            if (!this.iterationToIterationNodeMap.has(iteration)) {
                await this.loadIterationNode(iteration);
            }

            const currentIterationNode = this.iterationToIterationNodeMap.get(iteration);

            if (currentIterationNode) {
                this.currentIteration = currentIterationNode
                    ? { ...currentIterationNode, friendlyPath: iteration.replace(/\//g, "\\") /* replace all slashes with backslashes */ }
                    : undefined;
            }
        }
    };

    private constructIterationPath(iterationPath: string): string {
        // We need this format: "\<project name>\Iteration\<remaining iteration path>"
        const elements = iterationPath.split("/");
        const path = `\\${elements[0]}\\Iteration\\`;
        return path + elements.slice(1).join("\\");
    }

    private async loadIterationNode(iterationPath: string): Promise<void> {
        const projectId = getTFSPageData(this.pageContext)?.project?.id ?? "";
        if (projectId === "") {
            return;
        }
        const workItemTrackingClient = this.pageContext.getRestClient<IWorkItemTrackingRestClient>("IWorkItemTrackingRestClient");
        //it will be formatted to Sprint name\\Child sprint name; if any.
        //it basically drops project name from the path.
        const relativeIterationPath = iterationPath.split("/").slice(1).join("\\");

        let currentIterationNodePromise = this.iterationPromiseToIterationNodeMap.get(iterationPath);
        if (!currentIterationNodePromise) {
            currentIterationNodePromise = workItemTrackingClient.getClassificationNode(
                projectId,
                TreeStructureGroup.Iterations,
                relativeIterationPath,
                NodeConstants.maxNodeDepth
            );
            this.iterationPromiseToIterationNodeMap.set(iterationPath, currentIterationNodePromise);
        }

        const currentIterationNode = await currentIterationNodePromise;
        this.iterationToIterationNodeMap.set(iterationPath, currentIterationNode);
    }

    /** Determines whether there is a requirement/requirement hierarchy in this view and enables/disabled ordering accordingly */
    private async updateReorderRequirementStatus(ignoreBacklogConfigCache?: boolean) {
        this.missingWorkItems = [];
        this.isReorderDisabled = false;
        this.workItemsBlockingReorder = [];

        const config = await this.commonBacklogContentService.getBacklogConfiguration(ignoreBacklogConfigCache);
        if (!config) {
            return;
        }

        const workItemsBlockingReorderSet = new Set<number>();
        const allIdsToRowsMap = this.commonBacklogContentService.getAllIdsToRowsMap();
        this.isReorderDisabled = false;

        const parentMap = await this.getParentsMapping(allIdsToRowsMap, config);

        for (const [workItemId, workItem] of allIdsToRowsMap.entries()) {
            if (workItemId > 0) {
                // Work items with no parent or parent item on a different backlog level has real parent id of 0
                const parentId = Number(workItem.value.coreColumnMap[CoreFieldRefNames.Parent] ?? 0);
                const parentType = parentMap?.get(parentId);

                // Only task and requirement level items are included in work item ids. If a work item has a high level item parent (e.g. feature) it will return parentId = 0
                // Only items with parents can have same type hierarchy
                if (parentType && (this._isParentRequirement(parentType, config) || this._isParentTask(parentType, config))) {
                    const isRequirement = this._isWorkItemOfRequirementType(allIdsToRowsMap, config, workItemId);

                    const isSameProject = this.commonBacklogContentService.isParentInSameProject(workItemId, parentId);

                    // Because only tasks and requirements are included, if it's not a requirement it is a task.
                    // This is a nested item if...
                    //  1) It is a requirement and has a parent
                    // or
                    //  2) It is a task (not requirement) and its parent is a task (not requirement)
                    // and
                    //  3) Should be in the same project
                    const isNestedItem = isSameProject && (isRequirement || (!isRequirement && this._isParentTask(parentType, config)));

                    if (isNestedItem) {
                        this.isReorderDisabled = true;
                        workItemsBlockingReorderSet.add(workItemId);

                        // Requirement parents might not be filtered out of the list if they have task children.
                        // Don't list them missing if they're shown
                        const isParentShown = allIdsToRowsMap.has(parentId);
                        const isParentAlreadyIncluded = this.missingWorkItems.indexOf(parentId) !== -1;

                        // Add parent to the missing work items list, if it isn't already included and it isn't shown on the page
                        if (!isParentAlreadyIncluded && !isParentShown) {
                            this.missingWorkItems.push(parentId);
                        }
                    }
                }
            }
        }

        this.workItemsBlockingReorder = Array.from(workItemsBlockingReorderSet);
        workItemsBlockingReorderSet.clear();

        if (this.isReorderDisabled) {
            const problemWorkItemIds = this.workItemsBlockingReorder.join(", ");
            const missingWorkItemIds = this.missingWorkItems.join(", ");
            const disabledFeaturesString = missingWorkItemIds
                ? format(Resources.SameCategoryHierarchy_FeaturesBlockedWithWorkItems, missingWorkItemIds)
                : Resources.SameCategoryHierarchy_FeaturesBlocked;

            const renderMessage = () => {
                return (
                    <>
                        <span>{Resources.SameCategoryHierarchy_BacklogMessage}</span>
                        <span className="padding-left-4 font-weight-heavy">{disabledFeaturesString}</span>
                        <span className="padding-left-4">{format(Resources.SameCategoryHiearchy_ToFix, problemWorkItemIds)}</span>
                        <Link
                            className="padding-left-4"
                            href="https://go.microsoft.com/fwlink/?linkid=2007013"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {Resources.LearnMore}
                        </Link>
                    </>
                );
            };

            const messagesService = getBoardsMessagesService(this.pageContext);
            messagesService.dismissMessage();
            messagesService.showMessage({
                renderMessage: renderMessage,
                severity: MessageCardSeverity.Warning,
                containerName: ContainerNames.BacklogTab
            });
        }
    }

    private async getParentsMapping(
        allIdsToRowsMap: Map<number, IBacklogTreeRow>,
        backlogConfiguration: IBacklogConfiguration
    ): Promise<Map<number, string> | undefined> {
        if (!this.parentItems) {
            const projectId = getTFSProject(this.pageContext)?.id ?? "";
            const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
            const teamId = tfsPageService.getHorizontalNavigationTeamData()?.id ?? "";
            const allItemIds = Array.from(allIdsToRowsMap.keys()).filter(key => key >= 0);
            if (!allItemIds.length) {
                return;
            }

            const workClient = getWorkClient(this.pageContext);
            const parentItems = await workClient.getBoardMappingParentItems(
                { teamId, projectId } as TeamContext,
                backlogConfiguration.requirementBacklog.id,
                allItemIds
            );

            this.parentItems = new Map<number, string>();
            for (const parent of parentItems) {
                this.parentItems.set(parent.id, parent.workItemTypeName);
            }
        }

        return this.parentItems;
    }

    /**
     * Best effort determines if work item is of type requirement based off its id.
     */
    private _isWorkItemOfRequirementType(
        allIdsToRowsMap: Map<number, IBacklogTreeRow>,
        backlogConfiguration: IBacklogConfiguration,
        workItemId: number
    ): boolean {
        const workItemData = allIdsToRowsMap.get(workItemId);
        const workItemType = workItemData?.value.workItemTypeColorAndIcon?.workItemTypeName ?? "";
        return backlogConfiguration.requirementBacklog.workItemTypes.some(type => {
            return equals(type as any, workItemType, true);
        });
    }

    private _isParentRequirement = (workItemType: string, backlogConfiguration: IBacklogConfiguration) => {
        return backlogConfiguration.requirementBacklog.workItemTypes.some(type => {
            return equals(type as any, workItemType, true);
        });
    };
    private _isParentTask = (workItemType: string, backlogConfiguration: IBacklogConfiguration) => {
        return backlogConfiguration.taskBacklog.workItemTypes.some(type => {
            return equals(type as any, workItemType, true);
        });
    };
}

function flattenNodes(node: WorkItemClassificationNode) {
    const flatArray = [node];
    if (node.children) {
        node.children.forEach(child => {
            const flatChild = flattenNodes(child);
            flatArray.push(...flatChild);
        });
    }

    return flatArray;
}

Services.add("ISprintsHubContentService", { serviceFactory: SprintsHubContentService });
